import {
  Alert,
  Button,
  Drawer,
  Flex,
  Form,
  Input,
  theme,
  Typography
} from 'antd'
import { BookOpen, Check, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  OrganizationAuthMethodEnum,
  type OrganizationAuthMethod
} from 'src/api/main'
import { MonoValue, resolveIconColors } from 'src/components'

import { getProviderSetupInstructions } from '../../../organizations/components/OrganizationSettings/authMethodInstructions'

import { useStyles } from './styles'
import type { BuiltInProviderDescriptor } from './types'

const { Text, Title } = Typography

const isApple = (m: BuiltInProviderDescriptor['method']) =>
  m === OrganizationAuthMethodEnum.APPLE
const isEntra = (m: BuiltInProviderDescriptor['method']) =>
  m === OrganizationAuthMethodEnum.ENTRAID

type Props = {
  open: boolean
  descriptor: BuiltInProviderDescriptor
  saved?: OrganizationAuthMethod
  onClose: () => void
  /** True when the parent form has unsaved changes for any field. */
  saving?: boolean
  /** Optional: trigger a save from inside the drawer. */
  onSave?: () => void
}

export const ProviderSetupDrawer = ({
  open,
  descriptor,
  saved,
  onClose,
  saving,
  onSave
}: Props) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const tile = resolveIconColors(token, descriptor.iconColor)
  const { styles } = useStyles()

  const enabledFieldName = `${descriptor.key}_enabled`
  const clientIdFieldName = `${descriptor.key}_client_id`
  const tenantIdFieldName = `${descriptor.key}_tenant_id`
  const keyIdFieldName = `${descriptor.key}_key_id`
  const enabledValue = Form.useWatch<boolean | undefined>(enabledFieldName)

  const hasStoredSecret = Boolean(saved?.client_secret_masked)
  const apple = isApple(descriptor.method)
  const entra = isEntra(descriptor.method)

  const setupInstructions =
    descriptor.method !== 'email_otp'
      ? getProviderSetupInstructions(
          descriptor.method as OrganizationAuthMethodEnum
        )
      : null

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size={520}
      destroyOnHidden
      title={
        <Flex align="center" gap={10}>
          <span
            className={styles.providerIcon}
            style={{
              background: tile.backgroundColor,
              color: tile.iconColor,
              width: 32,
              height: 32
            }}
          >
            {descriptor.renderIcon(16)}
          </span>
          <Flex vertical style={{ lineHeight: 1.2 }}>
            <Title level={5} style={{ margin: 0, fontSize: 16 }}>
              {t(descriptor.i18nKey)}
            </Title>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t(`${descriptor.i18nKey}Subtitle`, { defaultValue: '' })}
            </Text>
          </Flex>
        </Flex>
      }
      footer={
        <Flex justify="space-between" align="center">
          {setupInstructions ? (
            <Button
              type="link"
              size="small"
              icon={<BookOpen size={14} />}
              href={setupInstructions.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ paddingInline: 0 }}
            >
              {t('workspace.authentication.providers.setupGuide')}
            </Button>
          ) : (
            <span />
          )}
          <Flex gap={8}>
            <Button onClick={onClose}>{t('cancel')}</Button>
            {onSave ? (
              <Button
                type="primary"
                icon={<Check size={14} />}
                loading={saving}
                onClick={onSave}
              >
                {t('workspace.authentication.actions.save')}
              </Button>
            ) : null}
          </Flex>
        </Flex>
      }
    >
      <Flex vertical gap={16}>
        {descriptor.needsApi ? (
          <Alert
            type="warning"
            showIcon
            title={t('workspace.authentication.providers.apiPendingLabel')}
            description={t('workspace.authentication.providers.apiPendingHint')}
          />
        ) : null}

        {!descriptor.hasCredentials && !descriptor.needsApi ? (
          <Alert
            type="info"
            showIcon
            title={t('workspace.authentication.providers.noConfigTitle')}
            description={t(
              `workspace.authentication.providers.noConfig.${descriptor.key}`,
              {
                defaultValue: t(
                  'workspace.authentication.providers.noConfigGeneric'
                )
              }
            )}
          />
        ) : null}

        {setupInstructions ? (
          <div className={styles.setupBlock}>
            <div className={styles.setupSubBlock}>
              <Text strong style={{ fontSize: 13 }}>
                {t('authMethods.setupInstructionsTitle')}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(setupInstructions.hintKey)}
              </Text>
            </div>
            <Flex align="center" gap={6} wrap="wrap">
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t('authMethods.docsLabel')}
              </Text>
              <a
                href={setupInstructions.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                {t('authMethods.openDocs')} <ExternalLink size={11} />
              </a>
            </Flex>

            <div className={styles.setupSubBlock}>
              <Text strong style={{ fontSize: 12 }}>
                {t('authMethods.redirectUrlsTitle')}
              </Text>
              <Flex vertical gap={8}>
                {setupInstructions.redirectUrlGroups.map((group) => (
                  <div key={group.labelKey} className={styles.setupSubBlock}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {t(group.labelKey)}
                    </Text>
                    <Flex vertical gap={4}>
                      {group.urls.map((url) => (
                        <Flex
                          key={url}
                          align="center"
                          gap={6}
                          className={styles.callbackRow}
                        >
                          <MonoValue>{url}</MonoValue>
                        </Flex>
                      ))}
                    </Flex>
                  </div>
                ))}
              </Flex>
            </div>
          </div>
        ) : null}

        {descriptor.hasCredentials ? (
          <div>
            <span className={styles.sectionLabel} style={{ marginTop: 0 }}>
              {t('workspace.authentication.providers.credentialsTitle')}
            </span>
            <Form.Item
              name={clientIdFieldName}
              label={
                apple
                  ? t('authMethods.appleServiceId')
                  : t('authMethods.clientId')
              }
              rules={
                enabledValue
                  ? [
                      {
                        required: true,
                        message: t('required_field_error')
                      }
                    ]
                  : undefined
              }
            >
              <Input
                placeholder={
                  descriptor.method === OrganizationAuthMethodEnum.GOOGLE
                    ? 'xxx.apps.googleusercontent.com'
                    : apple
                      ? 'com.example.service'
                      : undefined
                }
              />
            </Form.Item>
            <Form.Item
              name={`${descriptor.key}_client_secret`}
              label={
                apple
                  ? t('authMethods.applePrivateKey')
                  : t('authMethods.clientSecret')
              }
              rules={
                enabledValue && !hasStoredSecret
                  ? [
                      {
                        required: true,
                        message: t('required_field_error')
                      }
                    ]
                  : undefined
              }
              extra={
                hasStoredSecret
                  ? apple
                    ? t('authMethods.leaveEmptyToKeepKey')
                    : t('authMethods.leaveEmptyToKeep')
                  : null
              }
            >
              {apple ? (
                <Input.TextArea
                  rows={5}
                  placeholder={
                    hasStoredSecret
                      ? t('authMethods.leaveEmptyToKeepKey')
                      : '-----BEGIN PRIVATE KEY-----'
                  }
                  autoComplete="new-password"
                />
              ) : (
                <Input.Password
                  placeholder={
                    hasStoredSecret
                      ? t('authMethods.leaveEmptyToKeep')
                      : undefined
                  }
                  autoComplete="new-password"
                />
              )}
            </Form.Item>
            {entra || apple ? (
              <Form.Item
                name={tenantIdFieldName}
                label={
                  apple
                    ? t('workspace.authentication.providers.appleTeamId')
                    : t('authMethods.tenantId')
                }
                rules={
                  enabledValue
                    ? [
                        {
                          required: true,
                          message: t('required_field_error')
                        }
                      ]
                    : undefined
                }
              >
                <Input
                  placeholder={
                    apple
                      ? 'ABCDE12345'
                      : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
                  }
                />
              </Form.Item>
            ) : null}
            {apple ? (
              <Form.Item
                name={keyIdFieldName}
                label={t('authMethods.keyId')}
                rules={
                  enabledValue
                    ? [
                        {
                          required: true,
                          message: t('required_field_error')
                        }
                      ]
                    : undefined
                }
              >
                <Input placeholder="ABC123XYZ" />
              </Form.Item>
            ) : null}
          </div>
        ) : null}
      </Flex>
    </Drawer>
  )
}
